import React, { useEffect, useState } from "react"
import { Modal, Form, Input, Button, Select, Typography, Card, message, theme, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { createBoat, getCustomInfo, updateBoat } from "store/boats"
import { firstLoadingSelector } from "modules/loader-watchers/selectors"
import { CloseOutlined } from "@ant-design/icons"
import { MediaUpload } from "ui/MediaUpload"
import { MediaGrid } from "ui/MediaGrid/SortableMediaGrid"
import { MAX_MEDIA_SIZE } from "constants/form"
import { getStyles } from "./styles"
import { Text } from "ui/Typography"
import { useAiTextDescription } from "hooks/useAiTextDescription"
import { colors } from "themes/variables"
import { Icon } from "ui/Icon"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import MediaEditorModal from "ui/MediaEditorModal"

const { TextArea } = Input
const { Title } = Typography
const { useToken } = theme

export const AddResourceModal = ({ isVisible, onCancel, editingResource = null }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { token } = useToken()
  const styles = getStyles(token)
  const customInfoList = useSelector((state) => state.boats.custom_info.items) || []
  const customInfoFirstLoading = useSelector(firstLoadingSelector("boats.custom_info"))
  const [localMedia, setLocalMedia] = useState(editingResource?.properties || [])
  const [localMediaAttributes, setLocalMediaAttributes] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [mediaToCrop, setMediaToCrop] = useState(null)
  const [cropModalOpen, setCropModalOpen] = useState(false)

  const { descriptionValue, setDescriptionValue, isShowAiModal, setIsShowAiModal, profileDescription, setProfileDescription } =
    useAiTextDescription()

  const modalTitle = editingResource ? t("listings.tabs.resources.edit_resource") : t("listings.tabs.resources.add_resource")
  const activePhotosLength = localMedia.filter((photo) => !photo._destroy).length
  const hasPhotos = activePhotosLength > 0

  useEffect(() => {
    if (customInfoFirstLoading) {
      dispatch(getCustomInfo())
    }
  }, [customInfoFirstLoading, dispatch])

  useEffect(() => {
    if (editingResource) {
      form.setFieldsValue({
        name: editingResource.name,
        description: editingResource.description,
        characteristics: editingResource.custom_info || []
      })
      const initialMedia = editingResource.properties || []
      setLocalMedia(initialMedia)
      setLocalMediaAttributes(
        initialMedia.map(({ id, file_type, preview_url, blob, position }) => ({
          id,
          file_type,
          preview_url,
          blob,
          position,
          _destroy: false
        }))
      )
    } else {
      form.resetFields()
      setLocalMedia([])
      setLocalMediaAttributes([])
    }
  }, [editingResource, form])

  const handleDelete = (media) => {
    // For existing images
    if (media.id && typeof media.id === "number") {
      const updatedPhotos = localMedia.map((item) => (item.id === media.id ? { ...item, _destroy: true } : item))
      const updatedAttributes = updatedPhotos.map(({ id, file_type, preview_url, blob, _destroy, position, file }) => ({
        id,
        file_type,
        preview_url,
        blob,
        _destroy,
        position,
        file
      }))
      setLocalMedia(updatedPhotos)
      setLocalMediaAttributes(updatedAttributes)
    } else {
      // For newly uploaded images
      const filteredPhotos = localMedia.filter((item) => item.id !== media.id)
      const updatedPositions = filteredPhotos.map((item, index) => ({
        ...item,
        position: index
      }))
      setLocalMedia(updatedPositions)
      setLocalMediaAttributes(
        updatedPositions.map(({ id, file_type, preview_url, blob, file, _destroy, position }) => ({
          id,
          file_type,
          preview_url,
          blob,
          _destroy,
          file,
          position
        }))
      )
    }
  }

  const handleReorder = (newItems) => {
    setLocalMedia(
      newItems.map(({ id }, index) => ({
        ...localMedia.find((item) => item.id === id),
        position: index
      }))
    )
    setLocalMediaAttributes(
      newItems.map(({ id, file_type, preview_url, blob, _destroy, file }, index) => ({
        id,
        file_type,
        preview_url,
        blob,
        file,
        _destroy,
        position: index
      }))
    )
  }

  const onFileListChange = (photos, photosAttributes) => {
    const currentActivePhotos = localMedia.filter((photo) => !photo._destroy).length

    if (currentActivePhotos + photos.length > 5) {
      message.error(t("listings.tabs.resources.form.max_photos_error"))
      return
    }
    const oversizedPhotos = photos.filter((photo) => photo.size > 10 * 1024 * 1024)
    if (oversizedPhotos.length > 0) {
      message.error(t("listings.tabs.resources.form.photo_size_error"))
      return
    }
    const updatedPhotos = photos.map((photo, index) => ({
      ...photo,
      position: currentActivePhotos + index
    }))
    const updatedAttributes = photosAttributes.map((attr, index) => ({
      ...attr,
      position: currentActivePhotos + index
    }))

    setLocalMedia((prev) => [...prev, ...updatedPhotos])
    setLocalMediaAttributes((prev) => [...prev, ...updatedAttributes])
  }

  const handleSubmit = async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      const values = await form.validateFields()
      const properties_attributes = localMediaAttributes.map(({ id, file_type, blob, _destroy, position, file }) => ({
        file_type, _destroy,position,file,...(file === false ? { id } : {})
      }))
      const formData = new FormData()
      formData.append("boat[name]", values.name)
      formData.append("boat[description]", values.description)
      if (values.characteristics && values.characteristics.length > 0) {
        for (const characteristic of values.characteristics) {
          formData.append(`boat[custom_info][]`, characteristic)
        }
      }
      if (properties_attributes.length > 0) {
        for (const [index, attr] of properties_attributes.entries()) {
          for (const [key, value] of Object.entries(attr)) {
            if (value !== undefined) {
              formData.append(`boat[properties_attributes][${index}][${key}]`, value)
            }
          }
        }
      }
      await dispatch(editingResource ? updateBoat(editingResource.id, formData) : createBoat(formData))
      form.resetFields()
      setLocalMedia([])
      setLocalMediaAttributes([])
      onCancel()
    } catch (error) {
      console.error("Validation failed:", error)
    } finally {
      setIsSaving(false)
    }
  }
  
    useEffect(() => {
      form.setFieldsValue({ description: descriptionValue })
    }, [descriptionValue, form.setFieldsValue])

  const handleCancel = () => {
    form.resetFields()
    setLocalMedia([])
    setLocalMediaAttributes([])
    onCancel()
  }

  useEffect(() => {
    if (form.getFieldValue("description")) {
      setDescriptionValue(form.getFieldValue("description"))
    }
  }, [form.getFieldValue("description")])

  const handleCrop = (media) => {
    // Open unified editor for both images and videos
    setMediaToCrop(media)
    setCropModalOpen(true)
  }

  const handleCropComplete = async (croppedBlob) => {
    if (!croppedBlob || !mediaToCrop) return

    try {
      // Create a new file with a unique name
      const fileName = mediaToCrop.name || `cropped-${Date.now()}.jpg`
      const croppedFile = new File([croppedBlob], fileName, { type: "image/jpeg" })
      // Create a new URL for the cropped image
      const croppedUrl = URL.createObjectURL(croppedFile)

      setLocalMedia((prevMedia) =>
        prevMedia.map((item) =>
          item.id === mediaToCrop.id
            ? {
                ...item,
                file_type: "cropped_photo",
                file: croppedFile,
                url: croppedUrl,
                thumbnail_url: croppedUrl,
                // Clear any existing preview URLs
                preview_url: undefined,
                file_preview: undefined
              }
            : item
        )
      )
      // Also update attributes so submit sends the cropped file to API
      setLocalMediaAttributes((prevAttrs) =>
        prevAttrs.map((attr) =>
          attr.id === mediaToCrop.id
            ? {
                ...attr,
                file_type: "cropped_photo",
                file: croppedFile
                // Keep existing position and flags
              }
            : attr
        )
      )
      message.success(t("listings.tabs.media.image_updated_successfully"))
    } catch (error) {
      console.error("Error cropping image:", error)
      message.error(t("listings.tabs.media.crop_area_invalid"))
    } finally {
      setCropModalOpen(false)
      setMediaToCrop(null)
    }
  }

  return (
    <>
      <Modal
        title={modalTitle}
        open={isVisible}
        onCancel={handleCancel}
        width={1022}
        footer={null}
        closeIcon={<CloseOutlined style={styles.closeIcon} />}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Card className="mt-l mb-xl" style={styles.resourcePhotosCard}>
            <Title level={4} className="mb-l" style={styles.title}>
              {t("listings.tabs.resources.form.resource_photos")}
            </Title>

            {hasPhotos ? (
              <>
                <MediaUpload
                  className={styles.mediaUpload}
                  imageInfo={t("media_upload.image_size_limit", { maxSize: MAX_MEDIA_SIZE.image })}
                  onFileListChange={onFileListChange}
                  lastPosition={activePhotosLength}
                  previewSize={180}
                  gridGap={16}
                  maxFiles={5}
                  maxFileSize={10 * 1024 * 1024}
                />
                <div style={styles.mediaGrid}>
                  <MediaGrid
                    key={`media-grid-${localMedia.filter((photo) => !photo._destroy).length}`}
                    mediaItems={localMedia.filter((photo) => !photo._destroy)}
                    withActions
                    onDelete={handleDelete}
                    onReorder={handleReorder}
                    onCrop={handleCrop}
                    lastPosition={activePhotosLength}
                    previewSize={180}
                    gridGap={16}
                    sameSize
                    showHeroImageLabel
                  />
                </div>
              </>
            ) : (
              <MediaUpload
                onFileListChange={onFileListChange}
                imageInfo={t("media_upload.image_size_limit", { maxSize: MAX_MEDIA_SIZE.image })}
                lastPosition={0}
                previewSize={180}
                gridGap={16}
                maxFiles={5}
                maxFileSize={10 * 1024 * 1024}
              />
            )}
            <MediaEditorModal
              visible={cropModalOpen}
              onClose={() => {
                setCropModalOpen(false)
                setMediaToCrop(null)
              }}
              media={mediaToCrop}
              onImageCropComplete={handleCropComplete}
              targetWidth={1080}
              targetHeight={1080}
            />
          </Card>

          <Card style={styles.resourceInfoCard}>
            <Title level={4} style={styles.title}>
              {t("listings.tabs.resources.form.resource_information")}
            </Title>

            <Form.Item
              name="name"
              label={t("listings.tabs.resources.form.resource_title")}
              rules={[{ required: true, message: t("listings.tabs.resources.form.resource_title_required") }]}
            >
              <Input placeholder={t("listings.tabs.resources.form.resource_title_placeholder")} size="large" />
            </Form.Item>

            <Form.Item name="description" rules={[{ required: true, message: t("pro.ai_enhancement.description_too_short") }]}>
              <Flex justify="space-between" align="center" className="mb-s w-full">
                <Text className={styles.descriptionLabel}>{t("listings.tabs.resources.form.description")}</Text>
                <Button
                  color="primary"
                  variant="solid"
                  icon={<Icon name="aiEnhancement" color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled} />}
                  disabled={descriptionValue.length < 25}
                  onClick={() => setIsShowAiModal(true)}
                >
                  {t("pro.ai_enhancement.button_text")}
                </Button>
              </Flex>
              <TextArea
                placeholder={t("listings.tabs.resources.form.description_placeholder")}
                rows={4}
                size="large"
                onChange={(e) => setDescriptionValue(e.target.value)}
                value={descriptionValue}
              />
            </Form.Item>

            <Form.Item name="characteristics" label={t("listings.tabs.resources.form.characteristics")}>
              <Select
                mode="tags"
                size="large"
                placeholder={t("listings.tabs.resources.form.characteristics_placeholder")}
                options={customInfoList.map((info) => ({ label: info.name, value: info.name }))}
                tagRender={({ label, closable, onClose }) => (
                  <div style={styles.characteristicTag}>
                    {label}
                    {closable && (
                      <div onClick={onClose} style={styles.closeTagButton}>
                        <CloseOutlined style={styles.closeTagIcon} />
                      </div>
                    )}
                  </div>
                )}
                style={styles.selectContainer}
                listHeight={300}
                dropdownStyle={styles.selectDropdown}
                menuItemSelectedStyle={styles.selectMenuItem}
              />
            </Form.Item>
          </Card>

          <div style={styles.buttonsContainer}>
            <Button variant="outlined" color="primary" size="large" onClick={handleCancel} style={styles.cancelButton}>
              {t("listings.tabs.resources.form.cancel")}
            </Button>
            <Button type="primary" size="large" onClick={handleSubmit} style={styles.submitButton} disabled={isSaving}>
              {editingResource ? t("listings.tabs.resources.form.update") : t("listings.tabs.resources.form.save")}
            </Button>
          </div>
        </Form>
      </Modal>
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          profileDescription={profileDescription}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setIsShowAiModal(false)
          }}
          text={t("client.resources_content")}
          modalTitle={t("client.resources_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("client.imrove_with_ai_tooltip_for_resources")}
        />
      )}
    </>
  )
}
